import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, Calendar, MessageSquare, Edit3, Trash2, Eye, Download, Archive, Plus, Filter, SortAsc, X, MapPin, Clock, MessageCircle } from 'lucide-react';
import { useGetHellocianProjects } from '../../services/hellocian.order.service';

const ProjectsDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('active');
  const [showProjectMenu, setShowProjectMenu] = useState(null);
  const [showProjectDetails, setShowProjectDetails] = useState(null);

  const { data: allProjects = [], isLoading } = useGetHellocianProjects();

  const activeProjects = allProjects.filter(p => p.status === 'in-progress' || p.status === 'pending');
  const completedProjects = allProjects.filter(p => ['completed', 'cancelled', 'disputed'].includes(p.status));

  // Switch based on activeTab
  const currentProjects = activeTab === 'active' ? activeProjects : completedProjects;
  const totalProjects = currentProjects.length;
  const totalValue = currentProjects.reduce((sum, project) => sum + (project.price || 0), 0);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const url = new URL(window.location);
    url.searchParams.set('tab', tab);
    window.history.pushState({}, '', url);
  };

  const getProjectMenuItems = (project) => {
    const baseItems = [
      { 
        icon: Eye, 
        label: 'View Details', 
        action: () => {
            setShowProjectDetails(project);
        } 
      },
    ];

    if (activeTab === 'active') {
      return [
        ...baseItems,
        { 
            icon: MessageSquare, 
            label: 'Contact Client', 
            action: () => {
                const cid = project.chatId?._id || project.chatId;
                if (cid) navigate(`/hellocians/messages/${cid}`);
            } 
        }
      ];
    } else {
      return [
        ...baseItems,
        { icon: Download, label: 'Download Files', action: () => console.log('Download', project._id) },
        { icon: Trash2, label: 'Delete Project', action: () => console.log('Delete', project._id), danger: true }
      ];
    }
  };

  const ProjectCard = ({ project }) => (
    <div 
      onClick={() => setShowProjectDetails(project)}
    className="bg-white dark:bg-[#191c22] rounded-lg p-4 shadow-sm border border-gray-200 dark:border-[#32353c] hover:shadow-md transition-shadow relative cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <span className="text-blue-500 dark:text-blue-400 text-[10px] md:text-[12px] font-normal">{project.gigCategory?.join(', ') || 'Service'}</span>
          <h3 className="text-[13px] md:text-base text-gray-800 dark:text-white font-normal font-medium mt-1">{project.title}</h3>
          <p className="text-green-600 dark:text-green-400 font-medium md:font-semibold text-[13px] md:text-lg mt-2">${(project.price || 0).toFixed(2)}</p>
        </div>
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowProjectMenu(showProjectMenu === project._id ? null : project._id);
            }}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <MoreVertical size={16} className="text-gray-500 dark:text-gray-400" />
          </button>
          
          {showProjectMenu === project._id && (
            <div className="absolute right-0 top-10 bg-white dark:bg-[#191c22] border border-gray-200 dark:border-[#32353c] rounded-lg shadow-xl py-2 z-50 min-w-[160px]">
              {getProjectMenuItems(project).map((item, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    item.action();
                    setShowProjectMenu(null);
                  }}
                  className={`w-full flex items-center gap-1.5 px-2 py-1 text-[12px] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left ${
                    item.danger ? 'text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  <item.icon size={16} />
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400">
        <div className="flex items-center gap-1">
          <Calendar size={14} />
          <span className="md:text-sm text-[12px]">
            {activeTab === 'completed' && project.deliveredAt 
              ? `Completed ${new Date(project.deliveredAt).toLocaleDateString()}`
              : project.status === 'in-progress' ? 'In Progress' : project.status
            }
          </span>
        </div>
        <button 
           onClick={() => {
                const cid = project.chatId?._id || project.chatId;
                if (cid) navigate(`/hellocians/messages/${cid}`);
            }}
           className="flex items-center gap-1 hover:text-blue-500 transition-colors"
        >
          <MessageSquare size={14} />
          <span className="text-sm">
            {activeTab === 'active' ? 'Active Chat' : 'Review Chat'}
          </span>
        </button>
      </div>
    </div>
  );

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        setShowProjectMenu(null);
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const ProjectDetailsModal = ({ project, onClose }) => {
    if (!project) return null;

    const statusColors = {
        'pending': 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
        'in-progress': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
        'completed': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
        'cancelled': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
        'disputed': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
    };

    return (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/40 dark:bg-black/60 backdrop-blur-sm">
            <div className="bg-white dark:bg-[#1d2026] rounded-xl max-w-lg w-full shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200 flex flex-col max-h-[90vh]">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-[#32353c] flex items-center justify-between bg-gray-50/50 dark:bg-[#1d2026]/50">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white dark:bg-gray-700 rounded-md border border-gray-200 dark:border-gray-600 flex items-center justify-center text-[#174568] dark:text-blue-400">
                            <Eye size={17} />
                        </div>
                        <div>
                            <h3 className="text-[16px] font-medium text-gray-900 dark:text-white leading-tight">Project Details</h3>
                            <p className="text-xs text-gray-500 font-mono">#{project._id?.substring(project._id.length - 8).toUpperCase()}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-400 dark:text-gray-500">
                        <X size={20} />
                    </button>
                </div>

                <div className="overflow-y-auto p-4 space-y-4">
                    <div className="relative rounded-lg overflow-hidden h-44 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600">
                        <img 
                            src={project.img || project.gigId?.cover} 
                            alt="" 
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute top-3 left-3">
                            <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider shadow-sm ${statusColors[project.status] || 'bg-gray-100 text-gray-700'}`}>
                                {project.status}
                            </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="space-y-4">
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Project Title</label>
                                <h4 className="text-gray-900 dark:text-white text-[14px] font-medium leading-snug">{project.title}</h4>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Category</label>
                                <p className="text-gray-700 dark:text-gray-300 text-[14px] font-medium">{project.gigCategory?.join(', ') || 'N/A'}</p>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Package</label>
                                <p className="text-gray-700 dark:text-gray-300 text-[14px] font-medium capitalize">{project.pricingPackage}</p>
                            </div>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Deadline</label>
                                <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                                    <Calendar size={14} className="text-gray-400" />
                                    <span className="text-[14px] font-medium">{project.deliveryDate ? new Date(project.deliveryDate).toLocaleDateString() : 'N/A'}</span>
                                </div>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Price</label>
                                <p className="text-xl font-bold text-[#0077B6] dark:text-blue-400">${project.price?.toFixed(2)}</p>
                            </div>
                            <div>
                                <label className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block mb-1">Client</label>
                                <div className="flex items-center gap-2">
                                    <div className="w-6 h-6 bg-blue-50 dark:bg-blue-900/30 text-[#0077B6] dark:text-blue-400 rounded-full flex items-center justify-center text-[10px] font-bold border border-blue-100 dark:border-blue-800">
                                        {project.clientId?.firstName?.charAt(0) || 'C'}
                                    </div>
                                    <p className="text-gray-700 dark:text-gray-300 text-sm font-medium">{project.clientId?.firstName} {project.clientId?.lastName}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-[#1d2026]/50 border-t border-gray-100 dark:border-[#32353c] flex gap-3">
                    <button 
                        onClick={onClose}
                        className="flex-1 py-1.5 px-4 rounded-lg text-[13px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-600"
                    >
                        Close
                    </button>
                    <button 
                        onClick={() => {
                            const cid = project.chatId?._id || project.chatId;
                            if (cid) navigate(`/hellocians/messages/${cid}`);
                            onClose();
                        }}
                        className="flex-1 py-1.5 px-4 rounded-lg text-[13px] font-semibold bg-[#0077B6] dark:bg-blue-600 text-white hover:bg-[#005f91] dark:hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                    >
                        <MessageCircle size={16} />
                        Open Chat
                    </button>
                </div>
            </div>
        </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto p-3 bg-gray-50 dark:bg-[#0A0A0A] min-h-screen">
      {/* Tab Headers */}
      <div className="flex md:gap-4 md:mb-8 gap-2 mb-4">
        <div
          onClick={() => handleTabChange('active')}
          className={`flex-1 rounded-md p-3 md:p-3 cursor-pointer transition-all duration-300 ${
            activeTab === 'active'
              ? 'bg-green-500 text-white shadow-lg scale-105'
              : 'bg-blue-900 text-white hover:bg-blue-800'
          }`}
        >
          <p className="text-[11px] md:text-sm opacity-90 mb-1">Active Projects</p>
          <h2 className="text-[14px] md:text-[15px] font-medium md:font-semibold mb-3">
            Active Projects - {activeProjects.length} (${activeProjects.reduce((sum, p) => sum + (p.price || 0), 0).toFixed(0)})
          </h2>
          <button className="bg-white bg-opacity-20 text-white px-2 py-1 rounded-md text-[11px] md:text-sm font-normal md:font-medium hover:bg-opacity-30 transition-colors flex items-center gap-2">
            Active projects
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        <div
          onClick={() => handleTabChange('completed')}
          className={`flex-1 rounded-xl p-3 cursor-pointer transition-all duration-300 ${
            activeTab === 'completed'
              ? 'bg-green-500 text-white shadow-lg scale-105'
              : 'bg-blue-900 text-white hover:bg-blue-800'
          }`}
        >
          <p className="text-sm opacity-90 mb-1">Finished Task</p>
          <h2 className="text-[14px] md:text-[15px] font-medium md:font-semibold mb-3">
            Completed Projects - {completedProjects.length} (${completedProjects.reduce((sum, p) => sum + (p.price || 0), 0).toFixed(0)})
          </h2>
          <button className="bg-white bg-opacity-20 text-white px-2 py-1 rounded-md text-[11px] md:text-sm font-normal md:font-medium hover:bg-opacity-30 transition-colors flex items-center gap-2">
            Check archive
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Projects Container */}
      <div className="bg-white dark:bg-[#1d2026] rounded-xl shadow-sm">
        <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-[#32353c] relative">
          <h3 className="text-sm md:text-lg font-medium md:font-semibold text-gray-800 dark:text-white">
            {activeTab === 'active' ? 'Active Projects' : 'Completed Projects'}
          </h3>
          <div className="relative">
          
            
         
          </div>
        </div>

        <div className="p-3">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
               <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : (
            <>
              <div className="grid gap-4">
                {currentProjects.map((project) => (
                  <ProjectCard key={project._id} project={project} />
                ))}
              </div>
              
              {currentProjects.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-gray-400 dark:text-gray-500 mb-4">
                    <Calendar size={48} className="mx-auto" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-600 dark:text-gray-300 mb-2">
                    No {activeTab} projects
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    {activeTab === 'active' 
                      ? 'Start working on new projects to see them here.'
                      : 'Complete some projects to see them in this section.'
                    }
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-3 bg-white dark:bg-[#141414] rounded-xl p-3 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="text-center">
            <p className="text-[15px] md:text-3xl font-medium md:font-semibold text-gray-800 dark:text-white">{totalProjects}</p>
            <p className="text-gray-600 dark:text-gray-400">Total Projects</p>
          </div>
          <div className="text-center">
            <p className="text-[15px] md:text-3xl font-medium md:font-semibold text-green-600 dark:text-green-400">${totalValue.toFixed(0)}</p>
            <p className="text-gray-600 dark:text-gray-400">Total Value</p>
          </div>
          <div className="text-center">
            <p className="text-[15px] md:text-3xl font-medium md:font-semibold text-blue-600 dark:text-blue-400">
              ${(totalValue / Math.max(totalProjects, 1)).toFixed(0)}
            </p>
            <p className="text-gray-600 dark:text-gray-400">Average Value</p>
          </div>
        </div>
      </div>
      {showProjectDetails && (
        <ProjectDetailsModal 
            project={showProjectDetails} 
            onClose={() => setShowProjectDetails(null)} 
        />
      )}
    </div>
  );
};

export default ProjectsDashboard;